import React, { memo, useCallback, useMemo, type ComponentType, type ReactElement } from 'react';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import EmojiKeyboardIcon from '@atlaskit/icon/core/text-style';
import QuestionIcon from '@atlaskit/icon/core/question-circle';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	COMMAND_PALETTE_HELP_PRIORITY,
	COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY,
	COMMAND_PALETTE_SECTIONS,
} from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { useTransformNavigationCommands } from '@atlassian/jira-command-palette-common/src/common/utils/transform-navigation-commands/index.tsx';
import type { HideAndTransformNavigationCommandType } from '@atlassian/jira-command-palette-common/src/common/utils/transform-navigation-commands/types.tsx';
import { COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY } from '@atlassian/jira-command-palette-common/src/constants.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import type { KeywordGroups as CommandPaletteKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/types.tsx';
import type { RegisterInCommandPalette } from '@atlassian/jira-command-palette-common/src/ui/command-shortcuts/types.tsx';
import {
	type CreateAnalyticsEventType,
	keyboardShortcutPressed,
	productNavigationExpandedCollapsed,
} from '@atlassian/jira-common-navigation/src/keyboard-shortcuts/analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { HELP_PANEL_VERSION } from '@atlassian/jira-help-panel-common-constant/src/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	ModalsContainer,
	ModalsSubscriber,
} from '@atlassian/jira-modals-controller/src/components.tsx';
import type { ConsumerType as ProjectCreateDrawerConsumerType } from '@atlassian/jira-project-create-drawer-context/src/types.tsx';
import { ContainerContextSubscriber } from '@atlassian/jira-providers-container-context/src/main.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ApplicationPermissions } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import type { ApplicationPermissionsSubscriber as ApplicationPermissionsSubscriberDI } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useShallowStableReference } from '@atlassian/jira-utils-stable-reference/src/index.tsx';
import { ISSUE_SEARCH_PATH } from '../ops/index.tsx';
import {
	ANALYTICS_ACTION_KEYBOARD_SHORTCUTS,
	ANALYTICS_ACTION_VIEW_ALL_ISSUES,
	ANALYTICS_ACTION_CREATE_ISSUE,
	ANALYTICS_ACTION_GET_HELP,
} from './constants.tsx';
import messages from './messages.tsx';
import { getShortcutModifierKey } from './utils.tsx';

type Action = () => void;

type NavUIApi = {
	state: {
		isCollapsed: boolean;
		isResizing: boolean;
		isResizeDisabled: boolean;
		productNavWidth: number;
	};
	toggleCollapse: () => void;
	collapse: () => void;
	expand: () => void;
};

type KeyMap = {
	[key: string]: {
		label?: ReactElement;
		callback: (e?: KeyboardEvent) => void;
		registerInCommandPalette?: RegisterInCommandPalette;
	};
};

type ShortcutsProps = {
	label?: ReactElement | null;
	keyMap: KeyMap;
	isSpa?: boolean;
	location?: string;
};
export type Props = {
	shouldUseHelpShortcut: boolean;
	// Provided by withAnalyticsEvents HOC
	createAnalyticsEvent: CreateAnalyticsEventType;
	// eslint-disable-next-line jira/react/handler-naming
	navigateToIssueSearch: () => void;
	// eslint-disable-next-line jira/react/handler-naming
	navigateToMostRecentProject: () => void;
	showIssueCreate: Action;
	showSearch: Action;
	toggleHelpPanel: Action;
	ApplicationPermissionsSubscriber: typeof ApplicationPermissionsSubscriberDI;
	KeyboardShortcutsDialog: ComponentType<{
		applicationPermissionData: ApplicationPermissions;
		isOpen: boolean;
		onDialogDismissed: () => void;
		isNextGen?: boolean;
		shouldUseHelpShortcut?: boolean;
	}>;
	NavUIControllerSubscriber: ComponentType<{
		children: (navAPI: NavUIApi) => ReactElement;
	}>;
	ProjectCreateDrawerConsumer: ProjectCreateDrawerConsumerType;

	Shortcuts: ComponentType<ShortcutsProps>;
};

type PropsWithIntl = Props & {
	formatMessage: FormatMessage;
	hideAndTransformNavigationCommand: HideAndTransformNavigationCommandType;
	getKeywords: (keywordGroup: CommandPaletteKeywords) => string | undefined;
};

type getKeymapProps = {
	toggleHelpPanel: Action;
	openKeyboardShortcutsDialog: Action;
	toggleCollapse: Action;
	isNextGen?: boolean;
};

const KeyboardShortcuts = memo(
	({
		navigateToIssueSearch,
		navigateToMostRecentProject,
		showIssueCreate,
		showSearch,
		shouldUseHelpShortcut,
		formatMessage,
		getKeywords,
		createAnalyticsEvent,
		toggleHelpPanel: toggleHelpPanelFromProps,
		hideAndTransformNavigationCommand,
		ApplicationPermissionsSubscriber,
		KeyboardShortcutsDialog,
		ProjectCreateDrawerConsumer,
		Shortcuts,
		NavUIControllerSubscriber,
	}: PropsWithIntl) => {
		const getKeymap = useCallback(
			({
				toggleHelpPanel,
				openKeyboardShortcutsDialog,
				toggleCollapse,
				isNextGen,
			}: getKeymapProps): KeyMap => {
				const keys =
					isNextGen === true
						? {
								'?': {
									callback: openKeyboardShortcutsDialog,
									registerInCommandPalette: {
										name: formatMessage(messages.keyboardShortcutsCommandPalette),
										keywords: getKeywords('viewKeyboardShortcutsSynonyms'),
										priority: COMMAND_PALETTE_HELP_PRIORITY.viewKeyboardShortcuts,
										section: COMMAND_PALETTE_SECTIONS.help,
										components: {
											LeftIcon: () => (
												<EmojiKeyboardIcon
													spacing="spacious"
													label={
														fg('deliveroo_compal_a11y_fixes')
															? ''
															: formatMessage(messages.keyboardShortcutsCommandPalette)
													}
													aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
												/>
											),
										},
										analytics: {
											action: ANALYTICS_ACTION_KEYBOARD_SHORTCUTS,
										},
										id: 'keyboard-shortcuts.open',
									},
								},
								'/': {
									callback: showSearch,
								},
								'[': {
									callback: toggleCollapse,
								},
								escape: {
									// taken care of by the @atlaskit/drawer component itself
									// Replace with lodash/noop
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									callback: () => {},
								},
								'g p': {
									callback: () => navigateToMostRecentProject(),
								},
								'g i': {
									callback: () => navigateToIssueSearch(),
									registerInCommandPalette: hideAndTransformNavigationCommand({
										command: {
											id: 'find-issues',
											name: formatMessage(
												fg('jira-issue-terminology-refresh-m3')
													? messages.viewAllIssuesIssueTermRefresh
													: messages.viewAllIssues,
											),
											keywords: getKeywords('viewAllIssuesSynonyms'),
											priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.viewAllIssues,
											section: COMMAND_PALETTE_SECTIONS.siteNavigation,
											components: {
												LeftIcon: () => (
													<ArrowRightIcon
														spacing="spacious"
														label={
															fg('deliveroo_compal_a11y_fixes')
																? ''
																: formatMessage(
																		fg('jira-issue-terminology-refresh-m3')
																			? messages.viewAllIssuesIssueTermRefresh
																			: messages.viewAllIssues,
																	)
														}
														aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
													/>
												),
											},
											analytics: {
												action: ANALYTICS_ACTION_VIEW_ALL_ISSUES,
											},
										},
										hideCommandPath: ISSUE_SEARCH_PATH,
										path: ISSUE_SEARCH_PATH,
									}),
								},
								c: {
									callback: showIssueCreate,
									registerInCommandPalette: {
										name: formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.createIssueIssueTermRefresh
												: messages.createIssue,
										),
										keywords: getKeywords('createIssueSynonyms'),
										priority: COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY.createIssue,
										section: COMMAND_PALETTE_SECTIONS.quickActions,
										components: {
											LeftIcon: () => (
												<AddIcon
													spacing="spacious"
													label={
														fg('deliveroo_compal_a11y_fixes')
															? ''
															: formatMessage(
																	fg('jira-issue-terminology-refresh-m3')
																		? messages.createIssueIssueTermRefresh
																		: messages.createIssue,
																)
													}
													aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
												/>
											),
										},
										analytics: {
											action: ANALYTICS_ACTION_CREATE_ISSUE,
										},
										id: 'keyboard-shortcuts.create-issue',
									},
								},
								...(shouldUseHelpShortcut && __SPA__
									? {
											h: {
												callback: toggleHelpPanel,
												registerInCommandPalette: {
													name: formatMessage(messages.getHelp),
													keywords: getKeywords('getHelpSynonyms'),
													priority: COMMAND_PALETTE_HELP_PRIORITY.getHelp,
													section: COMMAND_PALETTE_SECTIONS.help,
													components: {
														LeftIcon: () => (
															<QuestionIcon
																spacing="spacious"
																label={
																	fg('deliveroo_compal_a11y_fixes')
																		? ''
																		: formatMessage(messages.getHelp)
																}
																aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
															/>
														),
													},
													analytics: {
														action: ANALYTICS_ACTION_GET_HELP,
													},
													id: 'keyboard-shortcuts.help',
												},
											},
										}
									: null),
							}
						: {
								'/': {
									callback: showSearch,
									label: <FormattedMessage {...messages.quickSearch} />,
								},
								escape: {
									// taken care of by the @atlaskit/drawer component itself
									// Replace with lodash/noop
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									callback: () => {},
									label: <FormattedMessage {...messages.closeDrawer} />,
								},
								c: {
									callback: showIssueCreate,
									label: (
										<FormattedMessage
											{...(fg('jira-issue-terminology-refresh-m3')
												? messages.createIssueIssueTermRefresh
												: messages.createIssue)}
										/>
									),
									registerInCommandPalette: {
										name: formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.createIssueIssueTermRefresh
												: messages.createIssue,
										),
										keywords: getKeywords('createIssueSynonyms'),
										section: COMMAND_PALETTE_SECTIONS.quickActions,
										priority: COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY.createIssue,
										components: {
											LeftIcon: () => (
												<AddIcon
													spacing="spacious"
													label={
														fg('deliveroo_compal_a11y_fixes')
															? ''
															: formatMessage(
																	fg('jira-issue-terminology-refresh-m3')
																		? messages.createIssueIssueTermRefresh
																		: messages.createIssue,
																)
													}
													aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
												/>
											),
										},
										analytics: {
											action: ANALYTICS_ACTION_CREATE_ISSUE,
										},
										id: 'keyboard-shortcuts.create-issue',
									},
								},
								'?': {
									callback: openKeyboardShortcutsDialog,
									label: <FormattedMessage {...messages.keyboardShortcuts} />,
									registerInCommandPalette: {
										name: formatMessage(messages.keyboardShortcutsCommandPalette),
										keywords: getKeywords('viewKeyboardShortcutsSynonyms'),
										priority: COMMAND_PALETTE_HELP_PRIORITY.viewKeyboardShortcuts,
										section: COMMAND_PALETTE_SECTIONS.help,
										components: {
											LeftIcon: () => (
												<EmojiKeyboardIcon
													spacing="spacious"
													label={
														fg('deliveroo_compal_a11y_fixes')
															? ''
															: formatMessage(messages.keyboardShortcutsCommandPalette)
													}
													aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
												/>
											),
										},
										analytics: {
											action: ANALYTICS_ACTION_KEYBOARD_SHORTCUTS,
										},
										id: 'keyboard-shortcuts.open',
									},
								},
								'g i': {
									callback: () => navigateToIssueSearch(),
									label: (
										<FormattedMessage
											{...(fg('jira-issue-terminology-refresh-m3')
												? messages.findIssuesIssueTermRefresh
												: messages.findIssues)}
										/>
									),
									registerInCommandPalette: hideAndTransformNavigationCommand({
										command: {
											id: 'find-issues',
											name: formatMessage(
												fg('jira-issue-terminology-refresh-m3')
													? messages.viewAllIssuesIssueTermRefresh
													: messages.viewAllIssues,
											),
											keywords: getKeywords('viewAllIssuesSynonyms'),
											priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.viewAllIssues,
											section: COMMAND_PALETTE_SECTIONS.siteNavigation,
											components: {
												LeftIcon: () => (
													<ArrowRightIcon
														spacing="spacious"
														label={
															fg('deliveroo_compal_a11y_fixes')
																? ''
																: formatMessage(
																		fg('jira-issue-terminology-refresh-m3')
																			? messages.viewAllIssuesIssueTermRefresh
																			: messages.viewAllIssues,
																	)
														}
														aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
													/>
												),
											},
											analytics: {
												action: ANALYTICS_ACTION_VIEW_ALL_ISSUES,
											},
										},
										hideCommandPath: ISSUE_SEARCH_PATH,
										path: ISSUE_SEARCH_PATH,
									}),
								},
								'g p': {
									callback: () => navigateToMostRecentProject(),
									label: (
										<FormattedMessage
											{...(expVal('project-terminology-refresh', 'isEnabled', false)
												? messages.browseAProjectGalaxia
												: messages.browseAProject)}
										/>
									),
								},
								'[': {
									callback: toggleCollapse,
									label: <FormattedMessage {...messages.toggleSidebar} />,
								},
								...(shouldUseHelpShortcut && __SPA__
									? {
											h: {
												callback: toggleHelpPanel,
												label: <FormattedMessage {...messages.openHelpPanel} />,
												registerInCommandPalette: {
													name: formatMessage(messages.getHelp),
													keywords: getKeywords('getHelpSynonyms'),
													priority: COMMAND_PALETTE_HELP_PRIORITY.getHelp,
													section: COMMAND_PALETTE_SECTIONS.help,
													components: {
														LeftIcon: () => (
															<QuestionIcon
																spacing="spacious"
																label={
																	fg('deliveroo_compal_a11y_fixes')
																		? ''
																		: formatMessage(messages.getHelp)
																}
																aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
															/>
														),
													},
													analytics: {
														action: ANALYTICS_ACTION_GET_HELP,
													},
													id: 'keyboard-shortcuts.help',
												},
											},
										}
									: null),
								[`${getShortcutModifierKey()}+k`]: {
									// Replace with lodash/noop
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									callback: () => {},
									label: <FormattedMessage {...messages.openCommandPalette} />,
									priority: -1, // this keyboard shortcut is only registered here to show up in the helper dialog. Its registration is in @atlassian/jira-command-palette-integration
								},
							};

				return keys;
			},
			[
				formatMessage,
				getKeywords,
				hideAndTransformNavigationCommand,
				navigateToIssueSearch,
				navigateToMostRecentProject,
				shouldUseHelpShortcut,
				showIssueCreate,
				showSearch,
			],
		);

		const createToggleCollapse = useCallback(
			(navAPI: NavUIApi) => {
				return () => {
					const {
						state: { isCollapsed },
						toggleCollapse,
					} = navAPI;

					keyboardShortcutPressed({
						createAnalyticsEvent,
						id: 'expandCollapseSidebar',
						key: '[',
					});
					productNavigationExpandedCollapsed({ createAnalyticsEvent, isCollapsed });
					toggleCollapse();
				};
			},
			[createAnalyticsEvent],
		);

		const createToggleHelpPanel = useCallback(
			(isNextGen: boolean) => {
				return () => {
					keyboardShortcutPressed({
						createAnalyticsEvent,
						id: 'openHelpPanel',
						key: 'h',
						attributes: {
							helpPanelVersion: HELP_PANEL_VERSION,
							helpPanelType: 'inProductHelp',
							nextGenProject: isNextGen,
						},
					});

					toggleHelpPanelFromProps();
				};
			},
			[createAnalyticsEvent, toggleHelpPanelFromProps],
		);

		const createOpenKeyboardShortcutsDialog = useCallback(
			(openDialog: () => void) => {
				return () => {
					keyboardShortcutPressed({
						createAnalyticsEvent,
						id: 'keyboardShortcuts',
						key: '?',
					});
					openDialog();
				};
			},
			[createAnalyticsEvent],
		);

		return (
			// @ts-expect-error - TS2745 - This JSX tag's 'children' prop expects type 'never' which requires multiple children, but only a single child was provided.
			<ApplicationPermissionsSubscriber>
				{/* @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. */}
				{({ data }) => (
					<ProjectCreateDrawerConsumer>
						{({ data: { isOpen } }) =>
							isOpen ? null : (
								<ModalsContainer scope="keyboard-shortcuts">
									<ModalsSubscriber>
										{(isKeyboardShortcutsDialogOpen, { open, close }) => (
											<ContainerContextSubscriber>
												{({ data: contextData }) => {
													const isNextGen =
														contextData && contextData.contextType === 'project'
															? contextData.project.isSimplified
															: false;

													return (
														<>
															{isKeyboardShortcutsDialogOpen && (
																<KeyboardShortcutsDialog
																	isOpen
																	onDialogDismissed={close}
																	applicationPermissionData={data.applicationPermissions}
																	isNextGen={isNextGen}
																	shouldUseHelpShortcut
																/>
															)}

															<NavUIControllerSubscriber>
																{(navAPI) => (
																	<ShortcutsInner
																		getKeymap={getKeymap}
																		navAPI={navAPI}
																		isNextGen={isNextGen}
																		createToggleHelpPanel={createToggleHelpPanel}
																		createOpenKeyboardShortcutsDialog={
																			createOpenKeyboardShortcutsDialog
																		}
																		open={open}
																		createToggleCollapse={createToggleCollapse}
																		Shortcuts={Shortcuts}
																	/>
																)}
															</NavUIControllerSubscriber>
														</>
													);
												}}
											</ContainerContextSubscriber>
										)}
									</ModalsSubscriber>
								</ModalsContainer>
							)
						}
					</ProjectCreateDrawerConsumer>
				)}
			</ApplicationPermissionsSubscriber>
		);
	},
);

const ShortcutsInner = memo(
	({
		getKeymap,
		navAPI,
		isNextGen,
		createToggleHelpPanel,
		createOpenKeyboardShortcutsDialog,
		open,
		createToggleCollapse,
		Shortcuts,
	}: {
		getKeymap: (props: getKeymapProps) => KeyMap;
		navAPI: NavUIApi;
		isNextGen: boolean;
		createToggleHelpPanel: (isNextGen: boolean) => Action;
		createOpenKeyboardShortcutsDialog: (openDialog: () => void) => Action;
		open: () => void;
		createToggleCollapse: (navAPI: NavUIApi) => Action;
		Shortcuts: ComponentType<ShortcutsProps>;
	}) => {
		const keyMap = useMemo(
			() =>
				getKeymap({
					toggleHelpPanel: createToggleHelpPanel(isNextGen),
					openKeyboardShortcutsDialog: createOpenKeyboardShortcutsDialog(open),
					toggleCollapse: createToggleCollapse(navAPI),
					isNextGen,
				}),
			[
				getKeymap,
				createToggleHelpPanel,
				isNextGen,
				createOpenKeyboardShortcutsDialog,
				open,
				createToggleCollapse,
				navAPI,
			],
		);
		const label = useMemo(
			() => (isNextGen ? null : <FormattedMessage {...messages.globalShortcuts} />),
			[isNextGen],
		);
		return <Shortcuts isSpa={__SPA__} label={label} keyMap={keyMap} />;
	},
);

export const KeyboardShortcutsWithFormatMessage = (unstableProps: Props) => {
	const props = useShallowStableReference(unstableProps, true);
	const { formatMessage } = useIntl();

	const { hideAndTransformNavigationCommand } = useTransformNavigationCommands();

	const { getKeywords } = useCommandKeywords();

	return (
		<KeyboardShortcuts
			formatMessage={formatMessage}
			hideAndTransformNavigationCommand={hideAndTransformNavigationCommand}
			getKeywords={getKeywords}
			{...props}
		/>
	);
};

export default KeyboardShortcutsWithFormatMessage;
